import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEvents, getAnnouncements, getSponsors, getSiteSettings } from '../../services/apiClient';
import type { Event, Announcement, Sponsor, SiteSettings } from '../../types';
import { formatDateRange, formatShortDate, truncate } from '../../lib/formatting';
import styles from './HomePage.module.css';

export function HomePage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [settings, setSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    getEvents().then((r) => r.data && setEvents(r.data.slice(0, 3)));
    getAnnouncements().then((r) => r.data && setAnnouncements(r.data.slice(0, 3)));
    getSponsors().then((r) => r.data && setSponsors(r.data));
    getSiteSettings().then((r) => r.data && setSettings(r.data));
  }, []);

  const platinumGold = sponsors.filter((s) =>
    ['platinum', 'gold'].includes(s.sponsorship_level)
  );

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className="container">
          <p className={styles.heroEyebrow}>Welcome</p>
          <h1 className={styles.heroTitle}>{settings?.display_name}</h1>
          <p className={styles.heroSub}>
            {settings
              ? truncate(
                  'Serving our community with events, programs, and opportunities for everyone.',
                  120
                )
              : ''}
          </p>
          <div className={styles.heroCtas}>
            <Link to="/events" className={styles.heroCta}>
              {settings?.default_cta_text ?? 'View Events'}
            </Link>
            <Link to="/about" className={styles.heroCtaSecondary}>
              About Us
            </Link>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className={styles.section}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Upcoming Events</h2>
            <Link to="/events" className={styles.sectionLink}>View all events →</Link>
          </div>
          <div className={styles.eventsGrid}>
            {events.map((event) => (
              <Link key={event.id} to={`/events/${event.slug}`} className={styles.eventCard}>
                <div className={styles.eventMeta}>
                  <span className={styles.eventType}>{event.type}</span>
                  {event.registration_open && (
                    <span className={styles.regOpen}>Registration Open</span>
                  )}
                </div>
                <h3 className={styles.eventTitle}>{event.title}</h3>
                <p className={styles.eventDate}>{formatDateRange(event.start_date, event.end_date)}</p>
                <p className={styles.eventLocation}>{event.location}</p>
                <p className={styles.eventDesc}>{truncate(event.description, 100)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements */}
      {announcements.length > 0 && (
        <section className={styles.sectionAlt}>
          <div className="container">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Latest News</h2>
              <Link to="/news" className={styles.sectionLink}>All announcements →</Link>
            </div>
            <div className={styles.newsList}>
              {announcements.map((ann) => (
                <div key={ann.id} className={styles.newsItem}>
                  <p className={styles.newsDate}>{formatShortDate(ann.publish_date)}</p>
                  <h3 className={styles.newsTitle}>{ann.title}</h3>
                  <p className={styles.newsBody}>{truncate(ann.body, 120)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sponsors */}
      {platinumGold.length > 0 && (
        <section className={styles.section}>
          <div className="container">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Our Sponsors</h2>
              <Link to="/sponsors" className={styles.sectionLink}>All sponsors →</Link>
            </div>
            <div className={styles.sponsorsRow}>
              {platinumGold.map((s) => (
                <a
                  key={s.id}
                  href={s.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.sponsorLogo}
                  title={s.name}
                >
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} />
                  ) : (
                    <span className={styles.sponsorName}>{s.name}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className={styles.ctaSection}>
        <div className="container">
          <h2 className={styles.ctaTitle}>Get in Touch</h2>
          <p className={styles.ctaSub}>Questions, memberships, or general inquiries — we're here to help.</p>
          <Link to="/contact" className={styles.ctaBtn}>Contact Us</Link>
        </div>
      </section>
    </div>
  );
}
